<template>
  <div v-if="groups.length" class="mt-6">
    <div class="d-flex align-center mb-1">
      <v-icon color="primary" class="mr-2">mdi-chart-timeline-variant</v-icon>
      <h3 class="text-h6">Evoluția ratingului</h3>
    </div>
    <p class="text--secondary mb-3">
      Folosește selectorul pentru a vedea modificarea produsă de fiecare cursă.
    </p>

    <v-tabs v-if="groups.length > 1" v-model="activeGroup" show-arrows class="mb-3">
      <v-tab v-for="group in groups" :key="group.simulator._id">
        {{ group.simulator.acronym || group.simulator.title }}
      </v-tab>
    </v-tabs>

    <template v-if="activeHistory">
      <v-card outlined>
        <v-card-text class="pa-3 pa-sm-5">
          <div class="rating-chart-frame">
            <div class="rating-y-axis text-caption text--secondary">
              <span v-for="(tick, index) in chartAxisTicks" :key="`tick-${index}`">{{ tick }}</span>
            </div>
            <div class="rating-plot">
              <div class="rating-grid" aria-hidden="true">
                <span v-for="(tick, index) in chartAxisTicks" :key="`grid-${index}`"></span>
              </div>
              <v-sparkline
                :value="chartRatings"
                :width="$vuetify.breakpoint.xsOnly ? 320 : 800"
                :height="$vuetify.breakpoint.xsOnly ? 120 : 180"
                color="#9e9e9e"
                :line-width="$vuetify.breakpoint.xsOnly ? 2 : 1"
                :padding="12"
                :smooth="false"
                auto-draw
                class="rating-sparkline"
              />
              <v-sparkline
                :value="chartRatings"
                :width="$vuetify.breakpoint.xsOnly ? 320 : 800"
                :height="$vuetify.breakpoint.xsOnly ? 120 : 180"
                color="primary"
                :line-width="$vuetify.breakpoint.xsOnly ? 2 : 1"
                :padding="12"
                :smooth="false"
                class="rating-sparkline rating-sparkline--selected"
                :style="selectedSparklineStyle"
              />
              <div
                class="rating-selection-line"
                :style="selectionLineStyle"
                aria-hidden="true"
              ></div>
            </div>
          </div>
          <div class="rating-x-axis text-caption text--secondary">
            <span>{{ firstDate }}</span>
            <span>{{ lastDate }}</span>
          </div>

          <v-slider
            v-model="selectedHistoryPosition"
            :max="activeHistory.changes.length"
            :min="0"
            :step="1"
            color="primary"
            track-color="grey lighten-2"
            thumb-label
            hide-details
            class="rating-slider mt-2"
            aria-label="Selectează cursa din istoricul ratingului"
          >
            <template v-slot:thumb-label="{ value }">#{{ value }}</template>
          </v-slider>
          <div class="rating-slider-labels d-flex justify-space-between text-caption text--secondary mt-1">
            <span>Start #0</span>
            <span>{{ selectedHistoryPosition === 0 ? 'Start #0' : `Cursa #${selectedHistoryPosition}` }}</span>
            <span>Cursa #{{ activeHistory.changes.length }}</span>
          </div>

          <v-divider class="my-3" />

          <div v-if="selectedChange" class="d-flex flex-column flex-sm-row align-sm-center">
            <div class="mr-sm-4 mb-2 mb-sm-0">
              <div class="text-caption text--secondary">{{ formatDate(selectedChange.occurredAt) }}</div>
              <router-link :to="`/event/${selectedChange.event._id}`" class="font-weight-bold">
                {{ selectedChange.event.title }}
              </router-link>
              <span class="text--secondary"> · {{ selectedChange.session.title || 'Cursă' }}</span>
              <div class="text-caption text--secondary mt-1">
                Performanță {{ signedChange(performanceChange(selectedChange)) }} ·
                Participare {{ signedChange(participationBonus(selectedChange)) }}
              </div>
            </div>
            <v-spacer />
            <div class="d-flex align-center">
              <span class="text-body-2 mr-3">
                {{ selectedChange.ratingBefore }} → <strong>{{ selectedChange.ratingAfter }}</strong>
              </span>
              <v-chip
                :color="changeColor(selectedChange.change)"
                dark
                small
                label
                class="rating-change-chip"
              >
                {{ signedChange(selectedChange.change) }}
              </v-chip>
            </div>
          </div>
          <div v-else class="d-flex align-center">
            <div>
              <div class="text-caption text--secondary">Înainte de prima cursă clasată</div>
              <strong>Rating inițial</strong>
            </div>
            <v-spacer />
            <div class="text-h6 font-weight-bold primary--text">{{ initialRating }}</div>
          </div>
        </v-card-text>
      </v-card>

      <h3 class="text-h6 mt-6 mb-3">Modificări pe curse</h3>
      <v-data-table
        :headers="headers"
        :items="historyNewestFirst"
        :items-per-page="10"
        :footer-props="footerProps"
        disable-sort
        dense
      >
        <template v-slot:[`item.occurredAt`]="{ item }">
          {{ formatDate(item.occurredAt) }}
        </template>
        <template v-slot:[`item.event`]="{ item }">
          <router-link :to="`/event/${item.event._id}`">{{ item.event.title }}</router-link>
          <div class="text-caption text--secondary">
            {{ item.season ? item.season.title : '' }} · {{ item.session.title || 'Cursă' }}
          </div>
        </template>
        <template v-slot:[`item.change`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                :color="changeColor(item.change)"
                dark
                x-small
                label
                class="rating-change-chip"
                v-bind="attrs"
                v-on="on"
              >
                {{ signedChange(item.change) }}
              </v-chip>
            </template>
            <span>
              Performanță {{ signedChange(performanceChange(item)) }} ·
              Participare {{ signedChange(participationBonus(item)) }}
            </span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.ratingAfter`]="{ item }">
          <strong>{{ item.ratingAfter }}</strong>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
export default {
  name: 'RlrRatingHistory',
  props: {
    history: {
      type: Array,
      default: () => []
    },
    initialSimulatorId: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    activeGroup: 0,
    selectedHistoryPosition: 0,
    headers: [
      {text: 'Data', value: 'occurredAt'},
      {text: 'Cursa', value: 'event'},
      {text: 'Modificare', value: 'change', align: 'center'},
      {text: 'Rating', value: 'ratingAfter', align: 'end'}
    ],
    footerProps: {
      'items-per-page-options': [10, 25, 50, -1],
      'items-per-page-text': 'Curse pe pagină',
      'items-per-page-all-text': 'Toate'
    }
  }),
  computed: {
    groups() {
      const bySimulator = {}
      const groups = []
      this.history.forEach(change => {
        if (!change.simulator) return
        const simulatorId = change.simulator._id
        if (!bySimulator[simulatorId]) {
          bySimulator[simulatorId] = {simulator: change.simulator, changes: []}
          groups.push(bySimulator[simulatorId])
        }
        bySimulator[simulatorId].changes.push(change)
      })
      groups.forEach(group => group.changes.sort((a, b) => {
        return new Date(a.occurredAt) - new Date(b.occurredAt)
          || a.ratedSessionNumber - b.ratedSessionNumber
      }))
      return groups.sort((first, second) => {
        const order = this.simulatorOrder(first.simulator) - this.simulatorOrder(second.simulator)
        return order || first.simulator.title.localeCompare(second.simulator.title)
      })
    },
    activeHistory() {
      return this.groups[this.activeGroup] || null
    },
    selectedChange() {
      if (!this.activeHistory || this.selectedHistoryPosition === 0) return null
      return this.activeHistory.changes[this.selectedHistoryPosition - 1] || null
    },
    chartRatings() {
      if (!this.activeHistory || !this.activeHistory.changes.length) return []
      return [this.activeHistory.changes[0].ratingBefore]
        .concat(this.activeHistory.changes.map(change => change.ratingAfter))
    },
    initialRating() {
      return this.activeHistory && this.activeHistory.changes.length
        ? this.activeHistory.changes[0].ratingBefore
        : 2500
    },
    chartMinimum() {
      return this.chartRatings.length ? Math.min.apply(null, this.chartRatings) : 0
    },
    chartMaximum() {
      return this.chartRatings.length ? Math.max.apply(null, this.chartRatings) : 0
    },
    chartAxisTicks() {
      const range = this.chartMaximum - this.chartMinimum
      return [0, 1, 2, 3, 4].map(index => {
        return Math.round(this.chartMaximum - (range * index / 4))
      })
    },
    selectionLineStyle() {
      if (!this.activeHistory || !this.activeHistory.changes.length) return {left: '12px'}
      const fraction = this.selectedHistoryPosition / this.activeHistory.changes.length
      const percentage = fraction * 100
      const pixelCorrection = 12 - (fraction * 24)
      return {left: `calc(${percentage}% + ${pixelCorrection}px)`}
    },
    selectedSparklineStyle() {
      if (!this.activeHistory || !this.activeHistory.changes.length) return {}
      const fraction = this.selectedHistoryPosition / this.activeHistory.changes.length
      const percentage = fraction * 100
      const pixelCorrection = 12 - (fraction * 24)
      const rightInset = `calc(${100 - percentage}% - ${pixelCorrection}px)`
      return {clipPath: `inset(0 ${rightInset} 0 0)`}
    },
    historyNewestFirst() {
      return this.activeHistory ? this.activeHistory.changes.slice().reverse() : []
    },
    firstDate() {
      return this.activeHistory ? this.formatDate(this.activeHistory.changes[0].occurredAt) : ''
    },
    lastDate() {
      if (!this.activeHistory) return ''
      return this.formatDate(this.activeHistory.changes[this.activeHistory.changes.length - 1].occurredAt)
    }
  },
  watch: {
    activeGroup() {
      this.selectLatestChange()
    },
    history() {
      this.selectPreferredGroup()
    },
    initialSimulatorId() {
      this.selectPreferredGroup()
    }
  },
  mounted() {
    this.selectPreferredGroup()
  },
  methods: {
    simulatorOrder(simulator) {
      const key = (simulator.acronym || simulator.title || '').toLowerCase()
      if (key === 'ir' || key === 'iracing') return 0
      if (key === 'ac') return 1
      if (key === 'acc') return 2
      return 3
    },
    selectPreferredGroup() {
      this.$nextTick(() => {
        const requestedIndex = this.groups.findIndex(group => {
          return group.simulator._id === this.initialSimulatorId
        })
        const iracingIndex = this.groups.findIndex(group => this.simulatorOrder(group.simulator) === 0)
        this.activeGroup = requestedIndex >= 0 ? requestedIndex : Math.max(iracingIndex, 0)
        this.selectLatestChange()
      })
    },
    selectLatestChange() {
      this.$nextTick(() => {
        this.selectedHistoryPosition = this.activeHistory
          ? this.activeHistory.changes.length
          : 0
      })
    },
    signedChange(change) {
      return change > 0 ? `+${change}` : `${change}`
    },
    performanceChange(change) {
      return change.performanceChange === undefined ? change.change : change.performanceChange
    },
    participationBonus(change) {
      return change.participationBonus || 0
    },
    changeColor(change) {
      if (change > 0) return 'success'
      if (change < 0) return 'error'
      return 'grey'
    },
    formatDate(value) {
      const date = new Date(value)
      const pad = number => String(number).padStart(2, '0')
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`
    }
  }
}
</script>

<style scoped>
.rating-sparkline {
  width: 100%;
  display: block;
  position: relative;
  z-index: 1;
}

.rating-sparkline--selected {
  position: absolute;
  top: 0;
  left: 0;
  transition: clip-path 0.08s linear;
}

.rating-chart-frame {
  display: flex;
}

.rating-y-axis {
  display: flex;
  flex: 0 0 48px;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 8px 10px 0;
  text-align: right;
}

.rating-plot {
  position: relative;
  min-width: 0;
  flex: 1 1 auto;
}

.rating-grid {
  position: absolute;
  z-index: 0;
  top: 12px;
  right: 0;
  bottom: 12px;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.rating-grid span {
  display: block;
  border-top: 1px solid rgba(120, 120, 120, 0.22);
}

.rating-selection-line {
  position: absolute;
  z-index: 2;
  top: 12px;
  bottom: 12px;
  width: 3px;
  background-color: #ff9800;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.85);
  pointer-events: none;
  transform: translateX(-1.5px);
  transition: left 0.08s linear;
}

.rating-selection-line::before,
.rating-selection-line::after {
  position: absolute;
  left: 50%;
  width: 9px;
  height: 9px;
  content: '';
  background-color: #ff9800;
  border: 2px solid white;
  border-radius: 50%;
  transform: translateX(-50%);
}

.rating-selection-line::before {
  top: -4px;
}

.rating-selection-line::after {
  bottom: -4px;
}

.rating-x-axis {
  display: flex;
  justify-content: space-between;
  margin-right: 12px;
  margin-left: 60px;
  padding-top: 2px;
}

.rating-slider-labels {
  margin-right: 12px;
  margin-left: 60px;
}

.rating-slider {
  margin-right: 4px;
  margin-left: 52px;
}

.rating-change-chip {
  min-width: 48px;
  justify-content: center;
}

@media (max-width: 600px) {
  .rating-y-axis {
    flex-basis: 42px;
    padding-right: 6px;
  }

  .rating-x-axis,
  .rating-slider-labels {
    margin-right: 12px;
    margin-left: 54px;
  }

  .rating-slider {
    margin-right: 4px;
    margin-left: 46px;
  }
}
</style>
